import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import AddCustomerModal from '../../components/ui/AddCustomerModal';
import DataTable from '../../components/ui/DataTable';
import { getCustomer, updateCustomer, deleteCustomer as apiDeleteCustomer } from '../../services/customers';
import { listLicences } from '../../services/licences';
import { listInvoices } from '../../services/invoices';
import { getStatusStyle } from '../../constants/statusConfig';

const CustomerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [licences, setLicences] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState([
    { label: 'Total Licences', value: '0', change: '', isPositive: true },
    { label: 'Active Licences', value: '0', change: '', isPositive: true },
    { label: 'Pending Requests', value: '0', change: '', isPositive: true },
    { label: 'Expired Licences', value: '0', change: '', isPositive: false }
  ]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const [customerRes, licencesRes, invoicesRes] = await Promise.all([
          getCustomer(id),
          listLicences({ customerId: id, page: 1, limit: 500 }),
          listInvoices({ customerId: id, page: 1, limit: 100 })
        ]);
        const c = customerRes?.data;
        setCustomerData(c);
        setCustomer({
          name: `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || '—',
          joinedDate: c?.createdAt ? new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—',
          email: c?.email || '—',
          phone: c?.phone || '—',
          role: c?.organization ? (typeof c.organization === 'object' ? c.organization.name || c.organization.organizationName : c.organization) : '—',
          lastSignIn: c?.lastLogin ? new Date(c.lastLogin).toLocaleString() : '—',
          purposeOfUse: c?.purposeOfUse || '—',
          passwordUpdate: '—',
          status: c?.status || (c?.isActive ? 'Active' : 'Inactive'),
          note: c?.note || ''
        });

        const list = licencesRes?.data ?? licencesRes?.licences ?? [];
        const arr = Array.isArray(list) ? list : [];
        setLicences(arr);

        const s = licencesRes?.stats;
        const total = s?.totalRequests ?? licencesRes?.pagination?.total ?? arr.length;
        const active = s?.approved ?? arr.filter((l) => l.status === 'Approved').length;
        const pending = s?.pending ?? arr.filter((l) =>
          ['Pending', 'Awaiting Review', 'Under Review', 'Re-review', 'Under Review – Additional Info Required', 'Reviewed', 'Ready for Signature', 'Terms & Conditions Signed'].includes(l.status)
        ).length;
        const expired = s?.expired ?? arr.filter((l) => l.status === 'Expired').length;
        setStats([
          { label: 'Total Licences', value: String(total), change: '', isPositive: true },
          { label: 'Active Licences', value: String(active), change: '', isPositive: true },
          { label: 'Pending Requests', value: String(pending), change: '', isPositive: true },
          { label: 'Expired Licences', value: String(expired), change: '', isPositive: false }
        ]);

        const invData = invoicesRes?.data;
        const invList = invData?.invoices ?? (Array.isArray(invoicesRes?.invoices) ? invoicesRes.invoices : []);
        setInvoices(Array.isArray(invList) ? invList : []);
      } catch (e) {
        console.error('Failed to load customer or licences', e);
        setCustomer(null);
        setCustomerData(null);
        setLicences([]);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const transactionRows = invoices.map((inv) => {
    const licenceName = inv.licenceId?.coaId?.name ?? inv.licenceId?.refNumber ?? '—';
    const typeMap = { PAID: 'Paid', AUTHORISED: 'Pending', DRAFT: 'Draft', SUBMITTED: 'Pending', REFUNDED: 'Refunded', VOIDED: 'Voided', DELETED: 'Voided' };
    const type = typeMap[inv.status] || inv.status || 'Pending';
    const dateStr = inv.date ? new Date(inv.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
    const amount = `${inv.currency || 'GBP'} ${Number(inv.total || 0).toFixed(2)}`;
    return {
      id: inv._id,
      _id: inv._id,
      invoiceNumber: inv.invoiceNumber || inv._id,
      licenceName,
      type,
      category: 'Invoice',
      date: dateStr,
      amount,
      invoice: inv
    };
  });

  const handleDeleteAccount = async () => {
    try {
      await apiDeleteCustomer(id);
      navigate('/customers');
    } catch (e) {
      console.error('Failed to delete customer', e);
      alert('Failed to delete customer');
      throw e;
    }
  };

  const handleUpdateCustomer = async (formData) => {
    try {
      setUpdating(true);
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        organization: formData.organization || undefined,
        purposeOfUse: formData.purposeOfUse || undefined,
        status: formData.status || undefined,
        note: formData.note || undefined
      };
      // Remove undefined values
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await updateCustomer(id, payload);
      setIsEditModalOpen(false);
      // Reload customer data
      const res = await getCustomer(id);
      const c = res?.data;
      setCustomerData(c);
      setCustomer({
        name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        joinedDate: c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—',
        email: c.email || '—',
        phone: c.phone || '—',
        role: c.organization || '—',
        lastSignIn: c.lastLogin ? new Date(c.lastLogin).toLocaleString() : '—',
        purposeOfUse: c.purposeOfUse || '—',
        passwordUpdate: '—',
        status: c.isActive ? 'Active' : (c.status || 'Inactive'),
        note: c.note || ''
      });
      alert('Customer updated successfully');
    } catch (e) {
      console.error('Update customer failed', e);
      alert(e?.data?.error || e?.message || 'Failed to update customer');
    } finally {
      setUpdating(false);
    }
  };

  const licenceRows = licences.map((l) => {
    const customerName = l.customerId
      ? [l.customerId.firstName, l.customerId.lastName].filter(Boolean).join(' ').trim() || l.contact?.fullName || '—'
      : l.contact?.fullName || '—';
    return {
      id: l._id,
      reviewer: l.reviewerId?.fullName || '—',
      approver: l.approverId?.fullName || '—',
      ref: l.refNumber || '—',
      name: customerName,
      licenceName: l.coaId?.name || '—',
      status: l.status || 'Pending',
      date: l.submittedAt || l.createdAt
        ? new Date(l.submittedAt || l.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '—'
    };
  });

  // Licence table columns
  const licenceColumns = [
    { header: 'Reviewer', accessor: 'reviewer', className: 'text-gray-900' },
    { header: 'Approver', accessor: 'approver', className: 'text-gray-900' },
    { header: 'Ref #', accessor: 'ref', className: 'text-gray-900' },
    { header: 'Name', accessor: 'name', className: 'text-gray-900' },
    { header: 'Licence Name', accessor: 'licenceName', className: 'text-gray-600' },
    {
      header: 'Status',
      accessor: 'status',
      render: (value, row) => {
        const status = value ?? row?.status;
        const { bg, text, label } = getStatusStyle(status);
        return (
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${bg} ${text} border-gray-200`}>
            {label || status}
          </span>
        );
      }
    },
    { header: 'Date', accessor: 'date', className: 'text-gray-600' }
  ];

  // Transaction table columns
  const transactionColumns = [
    {
      header: 'Transaction #',
      accessor: 'invoiceNumber',
      className: 'text-gray-900'
    },
    {
      header: 'Licence Name',
      accessor: 'licenceName',
      className: 'text-gray-600'
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (value, row) => {
        const typeConfig = {
          Paid: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
          Pending: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
          Draft: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
          Refunded: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
          Voided: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' }
        };
        const typeVal = value ?? row?.type;
        const config = typeConfig[typeVal] || typeConfig['Pending'];
        return (
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            {typeVal}
          </span>
        );
      }
    },
    {
      header: 'Category',
      accessor: 'category',
      className: 'text-gray-900'
    },
    {
      header: 'Date',
      accessor: 'date',
      className: 'text-gray-600'
    },
    {
      header: 'Amount',
      accessor: 'amount',
      className: 'text-gray-900'
    },
    {
      header: 'Action',
      accessor: 'action',
      render: (value, row) => (
        <button
          type="button"
          onClick={() => navigate(`/invoices/${row.id || row._id}`)}
          className="text-gray-400 hover:text-gray-600"
          title="View invoice"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10.8333 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V16.6667C2.5 17.1087 2.67559 17.5326 2.98816 17.8452C3.30072 18.1577 3.72464 18.3333 4.16667 18.3333H12.5C12.942 18.3333 13.366 18.1577 13.6785 17.8452C13.9911 17.5326 14.1667 17.1087 14.1667 16.6667V6.66667L10.8333 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.8333 2.5V6.66667H14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )
    }
  ];

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
        <button onClick={() => navigate('/customers')} className="hover:text-gray-900">
          ← Customer
        </button>
        <span>/</span>
        <span className="text-gray-900">Customer View Details</span>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M11.6667 1.66666H5.00001C4.55798 1.66666 4.13406 1.84225 3.82149 2.15481C3.50893 2.46737 3.33334 2.89129 3.33334 3.33332V16.6667C3.33334 17.1087 3.50893 17.5326 3.82149 17.8452C4.13406 18.1577 4.55798 18.3333 5.00001 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66666L11.6667 1.66666Z" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11.6667 1.66666V6.66666H16.6667" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.3333 10.8333H6.66666" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.3333 14.1667H6.66666" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.33334 7.5H7.50001H6.66668" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
            <p className="text-3xl font-semibold text-gray-900 mb-1">{stat.value}</p>
            <p className={`text-xs ${stat.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {stat.change}
            </p>
          </div>
        ))}
      </div>

      {/* Customer Info Section */}
      <div className="grid grid-cols-[1fr,320px] gap-6">
        {/* Left Card - Customer Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-3xl font-semibold text-gray-600">A</span>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-1">{customer?.name || '—'}</h2>
                <p className="text-sm text-gray-500">Joined: {customer?.joinedDate || '—'}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setIsEditModalOpen(true)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500"
              >
                Edit Customer
              </button>
              <button className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                Reset Password
              </button>
              <button 
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
              >
                Delete Account
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-8 gap-y-5">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Email Address</p>
              <p className="text-sm text-gray-900">{customer?.email || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Phone Number</p>
              <p className="text-sm text-gray-900">{customer?.phone || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Purpose of Use</p>
              <p className="text-sm text-gray-900">{customer?.purposeOfUse || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Role</p>
              <p className="text-sm text-gray-900">{customer?.role || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Last Sign In</p>
              <p className="text-sm text-gray-900">{customer?.lastSignIn || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Password Update</p>
              <p className="text-sm text-gray-900">{customer?.passwordUpdate || '—'}</p>
            </div>
          </div>
        </div>

        {/* Right Card - Customer Status and Note */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2.5">Customer Status</label>
            <div className="px-3.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-gray-50">
              {customer?.status || 'Active'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2.5">Note</label>
            <div className="px-3.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-gray-50 min-h-[60px]">
              {customer?.note || 'No notes'}
            </div>
          </div>
        </div>
      </div>

      {/* Licences Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Licences</h3>
        <DataTable
          data={licenceRows}
          columns={licenceColumns}
          showPagination={licenceRows.length > 10}
        />
      </div>

      {/* Transactions Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transactions</h3>
        <DataTable
          data={transactionRows}
          columns={transactionColumns}
          showPagination={transactionRows.length > 10}
        />
      </div>

      {/* Edit Customer Modal */}
      <AddCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onEdit={handleUpdateCustomer}
        initialData={customerData ? {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          email: customerData.email,
          phone: customerData.phone,
          organization: customerData.organization ? (typeof customerData.organization === 'object' ? customerData.organization.name || customerData.organization.organizationName : customerData.organization) : '',
          purposeOfUse: customerData.purposeOfUse,
          status: customerData.status || (customerData.isActive ? 'Active' : 'Inactive'),
          note: customerData.note
        } : null}
        isEdit={true}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Customer Account"
        message="Are you sure you want to delete this customer account? This action cannot be undone."
        itemName={customer?.name || ''}
      />
    </div>
  );
};

export default CustomerDetails;

